import { Link, useNavigate } from "react-router-dom";
import {
  GlobeAltIcon, Bars3Icon, XMarkIcon, StarIcon,
  BellIcon, HomeIcon, UserCircleIcon, ArrowLeftOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigation, NAV_ITEMS, getDashboardLabel } from "../hooks/useNavigation";
import { useTalentCart } from "../hooks/useTalentCart";
import NotificationBell from "../../features/notifications/components/NotificationBell";
import NavUserMenu from "./NavUserMenu";
import { useAuth } from "../../context";

// Single source of truth for the "Casting Brief Cart" star: both the desktop
// and mobile nav render this same component, so the client-only gate below
// can never drift out of sync between the two layouts.
function CastingCartButton({
  variant,
  cartCount,
  onClick,
  t,
}: {
  variant: "desktop" | "mobile";
  cartCount: number;
  onClick: () => void;
  t: (key: string) => string;
}) {
  if (variant === "desktop") {
    return (
      <motion.button
        onClick={onClick}
        whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors group"
        aria-label="Casting brief cart"
      >
        <StarIcon className="w-5 h-5 text-gray-700 group-hover:text-[#b2d12e] transition-colors" />
        <AnimatePresence>
          {cartCount > 0 && (
            <motion.span
              key="cart-badge"
              initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
              className="absolute -top-1 -end-1 bg-red-500 text-white text-xs min-w-[18px] h-[18px] rounded-full flex items-center justify-center font-bold px-1"
            >
              {cartCount > 99 ? "99+" : cartCount}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    );
  }

  return (
    <div className="border-t border-gray-200 mt-4 pt-4">
      <button onClick={onClick}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
        <StarIcon className="w-5 h-5 text-gray-700" />
        <span className="text-sm font-medium text-gray-700 flex-1 text-start">{t("nav.castingCart")}</span>
        {cartCount > 0 && (
          <span className="bg-red-500 text-white text-xs min-w-[20px] h-5 rounded-full flex items-center justify-center px-1">
            {cartCount > 99 ? "99+" : cartCount}
          </span>
        )}
      </button>
    </div>
  );
}

export default function Navigation() {
  const {
    isMobileMenuOpen, setIsMobileMenuOpen,
    isUserMenuOpen, setIsUserMenuOpen,
    isNotificationsOpen, setIsNotificationsOpen,
    isScrolled, isSigningOut, isRTL,
    userMenuRef, userMenuButtonRef,
    profile, authLoading, notifications, notificationsCount,
    avatarUrl, userDisplayName,
    t, language, dir, location,
    handleNotificationClick, handleSignOut,
    handleLanguageSwitch, handleNavigateDashboard, handleNavigateProfile,
  } = useNavigation();
  const { user } = useAuth();
  const isClient = user?.role === "client";
  const navigate = useNavigate();
  const { cartCount } = useTalentCart();
  const handleCartClick = () => {
    setIsMobileMenuOpen(false);
    navigate("/casting-brief");
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
        ? "backdrop-blur-xl bg-white/95 shadow-lg border-b border-gray-200"
        : "backdrop-blur-xl bg-white/80 border-b border-gray-200"
        }`}
      dir={dir}
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">

          {/* Logo */}
          <Link to="/" className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-[#b2d12e] focus:ring-offset-2 rounded-lg" aria-label="ARTERIA Home">
            <img src="/assets/arteria-logo.png" className="h-8 sm:h-10 lg:h-11 w-auto" alt="ARTERIA Logo" loading="eager" />
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-8" role="navigation" aria-label="Main navigation">
            {NAV_ITEMS.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}
                  className="relative text-sm font-medium group"
                  aria-current={isActive ? "page" : undefined}>
                  <span className={`transition-colors ${isActive ? "text-[#b2d12e]" : "text-gray-700 group-hover:text-[#b2d12e]"}`}>
                    {t(item.key)}
                  </span>
                  <span className={`absolute start-0 -bottom-2 h-[2px] bg-[#b2d12e] transition-all duration-300 ${isActive ? "w-full" : "w-0 group-hover:w-full"}`} />
                </Link>
              );
            })}
          </div>

          {/* Desktop right side */}
          <div className="hidden md:flex items-center gap-2 lg:gap-4">
            {/* Language switcher */}
            <button onClick={handleLanguageSwitch}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 text-gray-700 hover:text-[#b2d12e] transition-colors"
              aria-label={language === "en" ? "Switch to Arabic" : "Switch to English"}>
              <GlobeAltIcon className="w-5 h-5" />
              <span className="text-sm font-medium">{language === "en" ? "العربية" : "English"}</span>
            </button>

            {/* Talent casting cart */}
            {isClient && (
              <CastingCartButton variant="desktop" cartCount={cartCount} onClick={handleCartClick} t={t} />
            )}
            {authLoading && !profile ? (
              <div className="h-9 w-24 rounded-lg bg-gray-200 animate-pulse" aria-hidden="true" />
            ) : profile ? (
              <div className="flex items-center gap-2 lg:gap-3 border-s border-gray-200 ps-2 lg:ps-4">
                <NotificationBell />
                <NavUserMenu
                  profile={profile}
                  avatarUrl={avatarUrl}
                  userDisplayName={userDisplayName}
                  isOpen={isUserMenuOpen}
                  isSigningOut={isSigningOut}
                  isRTL={isRTL}
                  buttonRef={userMenuButtonRef}
                  panelRef={userMenuRef}
                  onToggle={() => setIsUserMenuOpen((p) => !p)}
                  onDashboard={handleNavigateDashboard}
                  onProfile={handleNavigateProfile}
                  onSignOut={handleSignOut}
                  t={t}
                />
              </div>
            ) : (
              <Link to="/login"
                className="bg-gradient-to-r from-[#b2d12e] to-[#a0c827] text-gray-900 px-4 lg:px-6 py-2.5 rounded-lg text-sm font-semibold hover:shadow-lg hover:shadow-[#b2d12e]/30 transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#b2d12e] focus:ring-offset-2">
                {t("nav.login")}
              </Link>
            )}
          </div>

          {/* Mobile actions */}
          <div className="md:hidden flex items-center gap-1">
            {profile && (
              <>
                <NotificationBell />
                <button
                  onClick={handleSignOut}
                  disabled={isSigningOut}
                  className="p-2 rounded-lg hover:bg-red-50 text-red-500 transition-colors focus:outline-none focus:ring-2 focus:ring-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label={isSigningOut ? t("common.signingOut") : t("nav.signOut")}
                  title={isSigningOut ? t("common.signingOut") : t("nav.signOut")}>
                  <ArrowLeftOnRectangleIcon className="w-6 h-6" />
                </button>
              </>
            )}
            {!profile && !authLoading && (
              <Link
                to="/login"
                className="px-3 py-2 rounded-lg bg-[#b2d12e] text-gray-900 text-sm font-semibold hover:bg-[#a0c827] transition-colors"
              >
                {t("nav.login")}
              </Link>
            )}
            <button
              onClick={() => setIsMobileMenuOpen((p) => !p)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-[#b2d12e]"
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}>
              {isMobileMenuOpen
                ? <XMarkIcon className="w-6 h-6" />
                : <Bars3Icon className="w-6 h-6" />
              }
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="md:hidden fixed inset-x-0 top-16 lg:top-20 bg-white border-t border-gray-200 shadow-lg z-40 overflow-y-auto max-h-[calc(100dvh-4rem)]"
            role="dialog" aria-label="Mobile menu" aria-modal="true">
            <div className="px-4 py-4 space-y-1">

              {/* Nav links */}
              {NAV_ITEMS.map((item) => (
                <Link key={item.path} to={item.path}
                  className={`block px-4 py-3 rounded-lg transition-colors font-medium ${location.pathname === item.path ? "bg-[#b2d12e]/10 text-[#b2d12e]" : "text-gray-700 hover:bg-gray-100 hover:text-[#b2d12e]"}`}>
                  {t(item.key)}
                </Link>
              ))}

              {/* Casting cart */}
              {isClient && (
                <CastingCartButton variant="mobile" cartCount={cartCount} onClick={handleCartClick} t={t} />
              )}

              {/* Language */}
              <div className="border-t border-gray-200 mt-4 pt-4">
                <button onClick={handleLanguageSwitch}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg hover:bg-gray-100 text-gray-700 hover:text-[#b2d12e] transition-colors">
                  <GlobeAltIcon className="w-5 h-5" />
                  <span className="text-sm font-medium">{language === "en" ? "العربية" : "English"}</span>
                </button>
              </div>

              {/* Auth section */}
              {authLoading && !profile ? (
                <div className="border-t border-gray-200 mt-4 pt-4">
                  <div className="h-16 rounded-lg bg-gray-200 animate-pulse" aria-hidden="true" />
                </div>
              ) : profile ? (
                <div className="border-t border-gray-200 mt-4 pt-4 space-y-2">
                  {/* User info */}
                  <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200">
                    <img src={avatarUrl} alt={userDisplayName} className="w-10 h-10 rounded-lg object-cover border-2 border-[#b2d12e]" loading="lazy" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{userDisplayName}</p>
                      <p className="text-xs text-gray-500 truncate">{profile.email}</p>
                      {profile.role && <span className="text-xs font-medium text-[#b2d12e] capitalize">{profile.role}</span>}
                    </div>
                  </div>

                  <button onClick={handleNavigateDashboard} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
                    <HomeIcon className="w-5 h-5 text-[#b2d12e]" />
                    <span className="text-sm font-medium text-gray-700">{getDashboardLabel(profile.role, t)}</span>
                  </button>

                  <button onClick={handleNavigateProfile} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
                    <UserCircleIcon className="w-5 h-5 text-[#b2d12e]" />
                    <span className="text-sm font-medium text-gray-700">{t("nav.profile")}</span>
                  </button>

                  {/* Notifications */}
                  <div>
                    <button onClick={() => setIsNotificationsOpen((p) => !p)}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors"
                      aria-expanded={isNotificationsOpen}>
                      <BellIcon className="w-5 h-5 text-gray-700" />
                      <span className="text-sm font-medium text-gray-700 flex-1 text-start">{t("nav.notifications")}</span>
                      {notificationsCount > 0 && (
                        <span className="bg-red-500 text-white text-xs min-w-[20px] h-5 rounded-full flex items-center justify-center px-1">
                          {notificationsCount > 99 ? "99+" : notificationsCount}
                        </span>
                      )}
                    </button>
                    <AnimatePresence>
                      {isNotificationsOpen && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                          className="mt-2 bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                          {notifications.length > 0 ? notifications.map((n) => (
                            <div key={n.id}
                              className={`p-3 bg-white border-b border-gray-100 last:border-0 cursor-pointer hover:bg-gray-50 transition-colors ${!n.isRead ? "border-s-2 border-s-blue-400" : ""}`}
                              onClick={() => handleNotificationClick(n.id, n.link)}
                              role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && handleNotificationClick(n.id, n.link)}>
                              <p className="text-xs font-medium text-gray-900">{n.title}</p>
                              <p className="text-xs text-gray-500 mt-1 line-clamp-2">{n.message}</p>
                            </div>
                          )) : (
                            <div className="p-4 text-center">
                              <p className="text-xs text-gray-400">{t("notifications.empty")}</p>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <button onClick={handleSignOut} disabled={isSigningOut}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-red-50 transition-colors text-red-500 disabled:opacity-50 disabled:cursor-not-allowed">
                    <ArrowLeftOnRectangleIcon className="w-5 h-5" />
                    <span className="text-sm font-medium">{isSigningOut ? t("common.signingOut") : t("nav.signOut")}</span>
                  </button>
                </div>
              ) : (
                <div className="border-t border-gray-200 mt-4 pt-4">
                  <Link to="/login"
                    className="block w-full text-center px-6 py-3 rounded-lg bg-gradient-to-r from-[#b2d12e] to-[#a0c827] text-gray-900 font-semibold hover:shadow-lg transition-all duration-300">
                    {t("nav.login")}
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
